import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { PlayIconButton } from "./play-icon-button";
import { SampleSpotLiveAsset } from "./sample-spot-live-asset";
import * as Styled from "./style";
import { useRouter } from "next/router";
import type { VideoSpotLiveSeasonType } from "~/components/blocks/product/type-guard";
import { isTouchDisplay } from "~/const/device";
import { PATHS } from "~/const/paths";
import { useHandleClickByDevice } from "~/hooks/use-handle-click-by-device";
import { useMatchMedia } from "~/hooks/use-match-media";
import { useOutsidePointerdown } from "~/hooks/use-outside-pointerdown";
import { useQueryString } from "~/hooks/use-query-string";

let timerId: number;

type VideoSpotLiveSeasonHeaderProps = {
  videoSpotLiveSeasonData: VideoSpotLiveSeasonType;
};

export const VideoSpotLiveSeasonHeader = memo((props: VideoSpotLiveSeasonHeaderProps) => {
  const { videoSpotLiveSeasonData } = props;

  const { push } = useRouter();
  const { season } = useQueryString();
  const { isSpSize } = useMatchMedia();
  const { handleClickByDevice } = useHandleClickByDevice();

  const [isPlayBtnDisplay, setIsPlayBtnDisplay] = useState(true);
  const showPlayBtn = useCallback(() => setIsPlayBtnDisplay(true), []);
  const hidePlayBtn = useCallback(() => setIsPlayBtnDisplay(false), []);

  const { ref: articleRef } = useOutsidePointerdown<HTMLElement>({
    onOutsidePointerDown: () => {
      if (isSpSize) return;
      hidePlayBtn();
    },
  });

  const {
    packageImage,
    keyVisualImage,
    keyVisualWithoutLogoImage,
    episodes: { edges },
  } = videoSpotLiveSeasonData;

  const episodeContentId = useMemo(() => {
    if (edges.length === 0) return "";
    return edges[0].node.id;
  }, [edges]);

  /** 視聴権利があるかどうかを判定する */
  const isStreamable = useMemo(() => {
    if (edges.length === 0) return false;
    return edges[0].node.viewingRights.isStreamable;
  }, [season]);

  const onPlayBtnPressed = useCallback(async () => {
    void push({
      pathname: PATHS.VOD_PLAYER,
      query: {
        season,
        content: episodeContentId,
        spot: "true",
      },
    });
  }, [episodeContentId]);

  useEffect(() => {
    // MEMO（kimura）: 予告動画を再生しない端末の場合、3秒後に再生/購入ボタンを自動で消すことでユーザーの混乱を招いたので以下の処理を記載する
    if (isSpSize) return;

    timerId = window.setTimeout(() => hidePlayBtn(), 3000);
    return () => clearTimeout(timerId);
  }, [isSpSize]);

  return (
    <Styled.ArtProductHeaderWrap
      ref={articleRef}
      onMouseOut={() => {
        if (isSpSize) return;
        hidePlayBtn();
      }}
      onMouseMove={() => {
        if (isPlayBtnDisplay || isSpSize) return;
        showPlayBtn();
      }}
      {...handleClickByDevice(() => {
        if (!isTouchDisplay || isSpSize) return;
        showPlayBtn();
      })}
    >
      <Styled.DivOverlay />

      <SampleSpotLiveAsset
        packageImage={packageImage}
        keyVisualImage={keyVisualImage}
        keyVisualWithoutLogoImage={keyVisualWithoutLogoImage}
      />

      <PlayIconButton
        isStreamable={isStreamable}
        isPlayBtnDisplay={isPlayBtnDisplay}
        onPlayBtnPressed={onPlayBtnPressed}
      />
    </Styled.ArtProductHeaderWrap>
  );
});

VideoSpotLiveSeasonHeader.displayName = "VideoSpotLiveSeasonHeader";
